import { DbEnum } from "../generated/prisma-enums";
import { isModelProvider } from "./model-provider.guard";

/**
 * Which model each provider's traffic lands on when a model config names a model the
 * catalog no longer carries. Every entry stays within its own provider — the user chose
 * that vendor, and a retirement is not a reason to leave it.
 *
 * Each entry must exist in models.json and the DB.
 *
 * Record<DbEnum.ModelProvider, ...> enforces exhaustiveness — adding a provider
 * to DbEnum.ModelProvider will cause a compile error here until its retirement
 * fallback is chosen.
 */
const RETIREMENT_FALLBACKS = {
  OPEN_AI: "gpt-4o",
  ANTHROPIC: "claude-sonnet-4-5",
  GOOGLE_AI: "gemini-3-flash-preview",
  KIMI: "kimi-k3",
  JEV: "jev-1.13.0"
} as const satisfies Record<DbEnum.ModelProvider, string>;

/**
 * Where a retired model's traffic lands, for a provider name the enum does not carry.
 * The only case that crosses providers, because a name outside the set has no own
 * provider to stay within.
 *
 * Deliberately reads OPEN_AI's entry rather than restating its value: the intent is
 * "whatever OpenAI retires onto", so re-pointing that entry should move this too. The
 * overload fallback side makes the opposite choice for its own unrecognized path, where the
 * target is a routing decision in its own right.
 */
const UNRECOGNIZED_PROVIDER_RETIREMENT_FALLBACK = RETIREMENT_FALLBACKS.OPEN_AI;

/** Where a retired model's traffic lands, within the same provider. */
export function retirementFallback(provider: DbEnum.ModelProvider | string): string {
  return isModelProvider(provider)
    ? RETIREMENT_FALLBACKS[provider]
    : UNRECOGNIZED_PROVIDER_RETIREMENT_FALLBACK;
}

export interface UnresolvedRetirementFallback {
  readonly provider: DbEnum.ModelProvider;
  readonly modelId: string;
}

/**
 * Which providers point at a retirement fallback the caller cannot resolve.
 *
 * The table above is compiled, so nothing verifies its values still name models
 * that exist — a vendor retiring one leaves the safety net pointing at nothing,
 * and the gap only surfaces when a retirement actually needs absorbing.
 *
 * Takes the resolver rather than calling one, because the answer differs by
 * caller: the static catalog is what a build can check, while a running backend
 * resolves against its DB-backed cache and is the only place the per-environment
 * answer exists.
 *
 * Walks the provider set rather than the table's own keys, so the answer covers
 * every provider by construction — a table that somehow lost an entry would be
 * reported rather than quietly skipped.
 */
export function unresolvedRetirementFallbacks(
  isResolvable: (modelId: string) => boolean
): readonly UnresolvedRetirementFallback[] {
  return Object.values(DbEnum.ModelProvider)
    .filter((provider) => !isResolvable(RETIREMENT_FALLBACKS[provider]))
    .map((provider) => ({ provider, modelId: RETIREMENT_FALLBACKS[provider] }));
}

export interface OverloadFallback {
  readonly modelName: string;
  readonly provider: DbEnum.ModelProvider;
}

const ANTHROPIC_OVERLOAD_FALLBACK = {
  modelName: "claude-sonnet-4-5",
  provider: DbEnum.ModelProvider.ANTHROPIC
} as const satisfies OverloadFallback;

const OPEN_AI_OVERLOAD_FALLBACK = {
  modelName: "gpt-4o",
  provider: DbEnum.ModelProvider.OPEN_AI
} as const satisfies OverloadFallback;

/**
 * Where each provider's traffic goes when that provider cannot serve the call
 * and a DIFFERENT vendor could — or `null` where no other vendor can stand in.
 * Every non-null target must be a *different* provider — re-sending to the one
 * that just refused defeats the purpose.
 *
 * 🔴 NOT ONLY CAPACITY. This table was named for overload (429/503/529) and read
 * only by `instanceof OverloadError`, so the one failure that most needs it
 * could never reach it: Anthropic reports a spent credit balance as a **400**,
 * and a 400 is not an overload on any path. That is NEX-3402 — a billing outage
 * on one vendor took every agent down while this table sat one `if` away,
 * already naming OpenAI as Anthropic's target. What may cross vendors is now
 * decided by `llmFailurePolicyFor(failure).overloadFallbackEligible`
 * (`shared/llm-failure/llm-failure-policy.ts`), and this table answers only the
 * second question: *where to* — including "nowhere".
 *
 * 🔴 `null` IS A DECISION, NOT A GAP. The failure policy is provider-blind: a
 * 429 or 529 is overload-fallback-eligible whoever sent it, so the only thing that keeps
 * a provider's traffic at home is this table saying there is no target. JEV is
 * the case today. It answers typed questions with a probability distribution; a
 * generative model answering the same call returns no distribution at all, and
 * costs roughly seventy times as much per input token — so a "rescued" call is a
 * different, dearer answer to a question the caller did not ask.
 *
 * Record<DbEnum.ModelProvider, ...> enforces exhaustiveness — adding a provider
 * to DbEnum.ModelProvider will cause a compile error here until its overload fallback
 * target is chosen, and choosing `null` has to be written out.
 */
export const OVERLOAD_FALLBACKS = {
  OPEN_AI: ANTHROPIC_OVERLOAD_FALLBACK,
  ANTHROPIC: OPEN_AI_OVERLOAD_FALLBACK,
  GOOGLE_AI: ANTHROPIC_OVERLOAD_FALLBACK,
  KIMI: ANTHROPIC_OVERLOAD_FALLBACK,
  JEV: null
} as const satisfies Record<DbEnum.ModelProvider, OverloadFallback | null>;

/**
 * Overload fallback for a provider name the enum does not carry. A separate
 * binding from OPEN_AI's entry, which points at the same target today:
 * re-pointing that entry leaves this one where it is.
 */
const UNRECOGNIZED_PROVIDER_OVERLOAD_FALLBACK = ANTHROPIC_OVERLOAD_FALLBACK;

/**
 * Which provider and model take over when this one cannot serve the call, or
 * `null` when no other vendor may. A caller that gets `null` rethrows the
 * original failure — it never picks a target of its own.
 */
export function overloadFallbackOf(
  currentProvider: DbEnum.ModelProvider | string
): OverloadFallback | null {
  return isModelProvider(currentProvider)
    ? OVERLOAD_FALLBACKS[currentProvider]
    : UNRECOGNIZED_PROVIDER_OVERLOAD_FALLBACK;
}

export function overloadFallbackTuningOf(stored: { readonly temperature?: number | null }): {
  readonly temperature?: number;
} {
  return stored.temperature === undefined || stored.temperature === null
    ? {}
    : { temperature: stored.temperature };
}
